// @ts-check

/**
 * @file Shared task-list filters for name, state, and sorting.
 */

import _ from "gettext-universal/build/src/translate.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import useBreakpoint from "responsive-breakpoints/build/use-breakpoint.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"

import Button from "../button"
import LabeledSelect from "../labeled-select"
import LabeledTextInput from "../labeled-text-input"
import {DEFAULT_TASK_SORT_KEY, parseTaskSortKey, taskSortOptions} from "../../shared/task-sort"
import {taskStateOptions} from "../../shared/task-state"

/** @import {NamedExoticComponent, ReactNode, RefObject} from "react" */
/** @typedef {import("react-native").TextInput} NativeTextInput */
/** @type {Record<string, object>} */
const styles = {}
const NAME_FILTER_DEBOUNCE_MS = 300

/**
 * @typedef {object} TaskFiltersValue
 * @property {string} name Name search text.
 * @property {import("../../shared/task-sort").TaskSortKey} sortKey Selected task sort key.
 * @property {string[]} states Selected task states, or an empty array for all states.
 */

/** @typedef {(value: TaskFiltersValue) => unknown} TaskFiltersChangeHandler */

/**
 * @typedef {object} TaskFiltersProps
 * @property {TaskFiltersChangeHandler} onChangeValue Filter change callback.
 * @property {string} [statePlaceholder] Placeholder shown when no states are selected.
 * @property {string} testID Stable test id prefix.
 * @property {TaskFiltersValue} value Active filter value.
 */

/**
 * @typedef {object} TaskFiltersState
 * @property {string} draftName Visible name filter value while the debounced callback waits.
 */

/**
 * Responsive task filters for reusable task lists.
 * @augments {ShapeComponent<TaskFiltersProps, TaskFiltersState>}
 */
class TaskFilters extends ShapeComponent {
  static propTypes = propTypesExact({
    onChangeValue: PropTypes.func.isRequired,
    statePlaceholder: PropTypes.string,
    testID: PropTypes.string.isRequired,
    value: PropTypes.exact({
      name: PropTypes.string.isRequired,
      sortKey: PropTypes.string.isRequired,
      states: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired
    }).isRequired
  })
  static defaultProps = {
    statePlaceholder: undefined
  }

  /** @type {ReturnType<typeof useBreakpoint>} */
  breakpoint = this.hookField()
  /** @type {boolean} */
  draftNamePrimed = false
  /** @type {RefObject<NativeTextInput | null>} */
  nameInputRef = createRef()
  /** @type {ReturnType<typeof setTimeout> | null} */
  nameDebounceTimer = null
  /** @type {TaskFiltersState} */
  state = {
    draftName: ""
  }

  /** Initializes locale, breakpoint, and controlled filter synchronization. */
  setup() {
    useLocale()
    this.breakpoint = useBreakpoint()
    if (!this.draftNamePrimed) {
      this.draftNamePrimed = true
      this.state.draftName = this.p.value.name
    }
    const valueName = this.p.value.name
    useEffect(() => {
      this.s.draftName = valueName
      syncTextInputValue(this.tt.nameInputRef, valueName)
    }, [valueName])
    useEffect(() => {
      return () => {
        if (this.tt.nameDebounceTimer) clearTimeout(this.tt.nameDebounceTimer)
      }
    }, [])
  }

  /**
   * Renders task filter controls.
   * @returns {ReactNode} Responsive filter controls.
   */
  render() {
    const {statePlaceholder, testID, value} = this.p
    const breakpoint = this.tt.breakpoint
    const breakpointName = breakpoint.name
    const filtersActive = Boolean(
      this.s.draftName.trim() ||
      value.states.length > 0 ||
      value.sortKey !== DEFAULT_TASK_SORT_KEY
    )
    return (
      <View
        style={styles[`container-${breakpointName}`] ||= breakpoint.styling({
          base: {
            alignItems: "stretch",
            flexDirection: "column",
            gap: 12,
            marginBottom: 16
          },
          lgUp: {
            alignItems: "flex-end",
            flexDirection: "row",
            flexWrap: "wrap"
          }
        })}
        testID={testID}
      >
        <View
          style={styles[`nameField-${breakpointName}`] ||= breakpoint.styling({
            base: {
              width: "100%"
            },
            lgUp: {
              flexBasis: 220,
              flexGrow: 2,
              minWidth: 180,
              width: undefined
            }
          })}
          testID={`${testID}/nameField`}
        >
          <LabeledTextInput
            defaultValue={value.name}
            inputRef={this.tt.nameInputRef}
            label={_("Name")}
            onChangeText={this.tt.onNameChangeText}
            placeholder={_("Search tasks...")}
            testID={`${testID}/nameInput`}
          />
        </View>
        <View
          style={styles[`stateField-${breakpointName}`] ||= breakpoint.styling({
            base: {
              width: "100%"
            },
            lgUp: {
              flexBasis: 160,
              flexGrow: 1,
              minWidth: 150,
              width: undefined
            }
          })}
          testID={`${testID}/stateField`}
        >
          <LabeledSelect
            label={_("State")}
            multiple
            onChangeValue={this.tt.onStateChangeValue}
            options={taskStateOptions()}
            placeholder={statePlaceholder || _("All states")}
            testID={`${testID}/stateSelect`}
            values={value.states}
          />
        </View>
        <View
          style={styles[`sortField-${breakpointName}`] ||= breakpoint.styling({
            base: {
              width: "100%"
            },
            lgUp: {
              flexBasis: 160,
              flexGrow: 1,
              minWidth: 150,
              width: undefined
            }
          })}
          testID={`${testID}/sortField`}
        >
          <LabeledSelect
            label={_("Sort")}
            onChangeValue={this.tt.onSortChangeValue}
            options={taskSortOptions()}
            testID={`${testID}/sortSelect`}
            values={[value.sortKey]}
          />
        </View>
        <View
          style={styles[`clearField-${breakpointName}`] ||= breakpoint.styling({
            base: {
              alignSelf: "flex-start",
              justifyContent: "center"
            },
            lgUp: {
              alignSelf: "flex-end",
              flexGrow: 0
            }
          })}
          testID={`${testID}/clearField`}
        >
          <Button
            disabled={!filtersActive}
            iconName="xmark"
            label={_("Clear filters")}
            onPress={this.tt.onClearFiltersPress}
            testID={`${testID}/clearButton`}
            variant="outline"
          />
        </View>
      </View>
    )
  }

  /**
   * Debounces name filter changes before notifying the parent list.
   * @param {string} name Name filter text.
   * @returns {void} Nothing.
   */
  onNameChangeText = (name) => {
    this.s.draftName = name
    if (this.nameDebounceTimer) clearTimeout(this.nameDebounceTimer)
    this.nameDebounceTimer = setTimeout(() => {
      if (name === this.p.value.name) return

      this.p.onChangeValue({
        ...this.p.value,
        name
      })
    }, NAME_FILTER_DEBOUNCE_MS)
  }

  /**
   * Applies the selected task sort.
   * @param {(string | number)[] | string | number | undefined} value Selected sort value.
   * @returns {void} Nothing.
   */
  onSortChangeValue = (value) => {
    const sortKey = parseTaskSortKey(selectedValue(value))
    if (sortKey === this.p.value.sortKey) return

    this.p.onChangeValue({
      ...this.p.value,
      sortKey
    })
  }

  /**
   * Applies the selected task state.
   * @param {(string | number)[] | string | number | undefined} value Selected state value.
   * @returns {void} Nothing.
   */
  onStateChangeValue = (value) => {
    const states = selectedValues(value)
    if (sameStringArray(states, this.p.value.states)) return

    this.p.onChangeValue({
      ...this.p.value,
      states
    })
  }

  /** Clears all active filters. */
  onClearFiltersPress = () => {
    if (this.nameDebounceTimer) clearTimeout(this.nameDebounceTimer)
    this.nameDebounceTimer = null
    this.s.draftName = ""
    syncTextInputValue(this.nameInputRef, "")
    if (
      !this.p.value.name &&
      this.p.value.states.length === 0 &&
      this.p.value.sortKey === DEFAULT_TASK_SORT_KEY
    ) {
      return
    }

    this.p.onChangeValue({
      name: "",
      sortKey: DEFAULT_TASK_SORT_KEY,
      states: []
    })
  }
}

/** @type {NamedExoticComponent<TaskFiltersProps>} */
const TaskFiltersComponent = memo(shapeComponent(TaskFilters))

export default TaskFiltersComponent

/**
 * Normalizes HayaSelect values into a single string value.
 * @param {(string | number)[] | string | number | undefined} value Raw select callback value.
 * @returns {string} Selected value, or an empty string.
 */
function selectedValue(value) {
  if (Array.isArray(value)) return value.length > 0 ? String(value[0]) : ""
  if (typeof value === "string" || typeof value === "number") return String(value)

  return ""
}

/**
 * Normalizes HayaSelect values into string values.
 * @param {(string | number)[] | string | number | undefined} value Raw select callback value.
 * @returns {string[]} Selected state values.
 */
function selectedValues(value) {
  const values = Array.isArray(value) ? value : value == null ? [] : [value]
  return values.map((entry) => String(entry))
}

/**
 * Compares two ordered string arrays.
 * @param {string[]} first First array to compare.
 * @param {string[]} second Second array to compare.
 * @returns {boolean} True when both arrays contain the same values in order.
 */
function sameStringArray(first, second) {
  if (first.length !== second.length) return false
  for (let index = 0; index < first.length; index++) {
    if (first[index] !== second[index]) return false
  }
  return true
}

/**
 * Syncs an uncontrolled visible text input to an external filter value.
 * @param {RefObject<NativeTextInput | null>} inputRef Input to update.
 * @param {string} value Value to show.
 * @returns {void} Nothing.
 */
function syncTextInputValue(inputRef, value) {
  const input = inputRef.current
  if (!input) return
  if (typeof input.setNativeProps === "function") {
    input.setNativeProps({
      text: value
    })
    return
  }
  if (!value && typeof input.clear === "function") {
    input.clear()
  }
}
